import {HttpClient} from '@angular/common/http'
import {computed, inject, Injectable, signal} from '@angular/core'
import {Observable, tap} from 'rxjs'
import {switchMap} from 'rxjs/operators'
import {environment} from '../../environments/environment'
import {InterestService} from './interest.service'
import {toObservable} from '@angular/core/rxjs-interop'
import {IHemnetData} from '../application/types'

@Injectable({
  providedIn: 'root'
})
export class HemnetService {
  public savedId = signal<string | null>(null)

  // These are the only values of interest.
  public askingPrice = signal(0)
  public interestRate = signal(0)
  public downPayment = signal(0)
  public runCost = signal(0)
  public fee = signal(0)
  public valid = signal(false)

  // After this date, the flag and this check can be removed.
  public downPaymentPercent = signal(0.1)


  public minimumDownPayment = computed(() =>
    this.askingPrice() * this.downPaymentPercent())
  private readonly pHemnetData = signal<IHemnetData>({} as IHemnetData)
  public hemnetData = this.pHemnetData.asReadonly()

  public hemnetData$ = toObservable(this.pHemnetData)

  private readonly interestService = inject(InterestService)
  private readonly httpClient = inject(HttpClient)

  /**
   * Fetch the hemnet data from our backend and publish the result.
   */
  public getHemnetData(objectId: string): void {
    const url = `${environment.hemnetServiceUrl}/hemnet/${objectId}`
    const propertiesToConvert = ['downPayment', 'askingPrice', 'runCost', 'fee', 'year']
    this.httpClient.get<IHemnetData>(url)
      .pipe(
        tap(data => {
          // Convert props
          propertiesToConvert.forEach(prop => data[prop] = Number(data[prop]))
          this.askingPrice.set(data.askingPrice)
          this.downPayment.set(data.downPayment)
          this.fee.set(data.fee)
          this.runCost.set(data.runCost!)
          // This is set only once
          this.pHemnetData.set(data)
        }),
        switchMap(() => this.interestService.getLatestInterest())
      )
      .subscribe()
  }


  /**
   * Saves hemnet data on server
   */
  public saveHemnetData(): Observable<IHemnetData> {
    const data = {
      ...this.hemnetData(),
      askingPrice: this.askingPrice(),
      downPayment: this.downPayment(),
      fee: this.fee(),
      runCost: this.runCost()
    }
    const url = `${environment.hemnetServiceUrl}/loanpromise`
    return this.httpClient.put<IHemnetData>(url, data).pipe(
      tap(hemnetData => this.savedId.set(hemnetData.id ?? null))
    )
  }
}
